import { useCallback, useRef, useState } from 'react';
import { apiDownloadBlob, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { unwrapData } from '@wapharma/shared';
import { getToken } from '../auth';
import { isImportFile } from './useProductBulkImport';

export type RoleImportRowStatus = 'ready' | 'invalid' | 'exists' | 'skipped';

export type RoleImportPreviewRow = {
  row: number;
  status: RoleImportRowStatus;
  name: string;
  packs: string[];
  packLabels: string[];
  parentRoleName: string | null;
  issues: string[];
};

export type RoleImportPreview = {
  rowCount: number;
  summary: {
    ready: number;
    invalid: number;
    exists: number;
    skipped: number;
  };
  rows: RoleImportPreviewRow[];
  canCommit: boolean;
};

export type RoleImportCommitResult = {
  created: Array<{
    row: number;
    id: string;
    name: string;
    packs: string[];
    parentRoleName: string | null;
  }>;
  errors: Array<{ row: number; message: string }>;
};

export async function downloadRolesImportTemplate(token: string, format: 'xlsx' | 'csv') {
  const filename = format === 'csv' ? 'modele-import-roles.csv' : 'modele-import-roles.xlsx';
  await apiDownloadBlob(`/admin/roles/import/template?format=${format}`, token, filename);
}

export function useRolesBulkImport(opts?: {
  onCommitted?: (result: RoleImportCommitResult) => void;
}) {
  const token = getToken();
  const fileRef = useRef<HTMLInputElement>(null);
  const fileStateRef = useRef<File | null>(null);
  const onCommittedRef = useRef(opts?.onCommitted);
  onCommittedRef.current = opts?.onCommitted;

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RoleImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<RoleImportCommitResult | null>(null);

  const reset = useCallback(() => {
    fileStateRef.current = null;
    setFile(null);
    setPreview(null);
    setErr(null);
    setPickErr(null);
    setCommitResult(null);
    setPreviewLoading(false);
    setCommitting(false);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const runPreview = useCallback(async (next: File) => {
    if (!token) {
      setErr('Session expirée. Reconnectez-vous pour analyser le fichier.');
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    setErr(null);
    setCommitResult(null);
    try {
      const form = new FormData();
      form.append('file', next);
      const raw = await apiPostForm<{ data: RoleImportPreview }>(
        '/admin/roles/import/preview',
        form,
        token,
      );
      setPreview(unwrapData(raw) as RoleImportPreview);
    } catch (e: unknown) {
      setPreview(null);
      setErr(getDisplayError(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [token]);

  const applyFile = useCallback(
    (next: File | null) => {
      setPickErr(null);
      setErr(null);
      setCommitResult(null);
      setPreview(null);
      if (!next) {
        fileStateRef.current = null;
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
      if (!isImportFile(next)) {
        setPickErr('Format accepté : .xlsx, .xls ou .csv');
        fileStateRef.current = null;
        setFile(null);
        return;
      }
      fileStateRef.current = next;
      setFile(next);
      void runPreview(next);
    },
    [runPreview],
  );

  const commit = useCallback(async () => {
    const current = fileStateRef.current;
    if (!current) {
      setErr('Fichier requis.');
      return;
    }
    if (!token) {
      setErr('Session expirée.');
      return;
    }
    setCommitting(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', current);
      const raw = await apiPostForm<{ data: RoleImportCommitResult }>(
        '/admin/roles/import/commit',
        form,
        token,
      );
      const result = unwrapData(raw) as RoleImportCommitResult;
      setCommitResult(result);
      onCommittedRef.current?.(result);
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setCommitting(false);
    }
  }, [token]);

  const busy = previewLoading || committing;
  const zoneDisabled = !token || busy;
  const canCommit = Boolean(preview?.canCommit) && !busy && !commitResult;

  return {
    token,
    fileRef,
    file,
    preview,
    previewLoading,
    committing,
    busy,
    err,
    pickErr,
    commitResult,
    zoneDisabled,
    canCommit,
    reset,
    applyFile,
    commit,
  };
}
